import { Stack } from '@mui/material'
import { useState } from 'react'
import { IconButton } from 'utoniq-ui'

import { CollectionBoxFragment, Maybe } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenu, SideMenuProps } from '@/components/layouts'
import { CollectionBox, CollectionBoxSkeleton } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  artistName: string
  hasNextPage: boolean
  artistId: string
  user: Maybe<SideMenuProps['user']>
  userCollections: Maybe<{
    edges: { node: CollectionBoxFragment }[]
  }>
  liveStreamId: string | undefined
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  isArtist: Maybe<SideMenuProps['isArtist']>
}

type CollectionBannerCellProps = Pick<State, 'userCollections'> & {
  loading: boolean
  artistId: string
  hasNextPage: boolean
  onFetchMore: () => Promise<void>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMoreArtistCollection: () => Promise<void>
}

export const ArtistCollectionsTemplate: Template<State, Action> = ({
  state,
  onFetchMoreArtistCollection,
}) => {
  const { t } = useTranslation('artistCollections')
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)
  const { routerNames } = useRouter()

  return (
    <ArtistThemeProvider>
      <BaseLayout
        navigation={{
          type: 'artist',
          props: {
            isLive: !!state.liveStreamId,
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
          },
        }}
        margin
        header={{
          title: t('title'),
          left: config.applicationType === 'organization' && (
            <IconButton.AvatarNoImage
              edge="start"
              onClick={() => setOpenSideMenu(!openSideMenu)}
              badge={
                state.user?.hasManagementTask ||
                !!state.user?.unreadNotificationCount
              }
              color="icon.line"
              bgcolor="icon.background"
            />
          ),
        }}
      >
        <Head title={[routerNames.ArtistCollections, state.artistName]} />
        <CollectionBannerCell
          userCollections={state.userCollections}
          loading={state.loading}
          artistId={state.artistId}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMoreArtistCollection}
        />
        <SideMenu
          followArtists={state.followArtists}
          memberArtists={state.memberArtists}
          open={openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={state.user}
          isArtist={state.isArtist}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const CollectionBannerCell: Cell<CollectionBannerCellProps> = ({
  loading,
  artistId,
  userCollections,
  onFetchMore,
  hasNextPage,
}) => {
  const { t } = useTranslation('artistCollections')
  const { analytics } = useAnalytics()
  return (
    <CellFrame sx={{ py: 2 }}>
      <ScrollFetcher
        loading={loading}
        onFetchMore={onFetchMore}
        hasNextPage={hasNextPage}
        componentProps={{ spacing: 5 }}
        emptyMessage={t('collectionEmptyMessage')}
        nodes={userCollections?.edges.map(({ node: collection }, index) => (
          <CollectionBox
            key={index}
            collection={collection}
            fullWidth
            sx={{
              height: 'calc((100vw - 40px) * 190 / 335)',
              maxHeight: 'calc(560px * 190 / 335)',
            }}
            actionAreaProps={{
              onClick() {
                analytics?.track('click_select', {
                  applicationKey: config.applicationKey,
                  artistId,
                  collectionId: collection?.id,
                  name: 'artistCollections',
                })
              },
            }}
          />
        ))}
        loadingComponent={
          <Stack spacing={5}>
            {Array.from(Array(3).keys()).map(key => (
              <CollectionBoxSkeleton fullWidth key={key} />
            ))}
          </Stack>
        }
      />
    </CellFrame>
  )
}
